import { useQuery } from '@tanstack/react-query';
import {
  faUsers,
  faGears,
  faClipboardList,
  faUserCheck,
  faHourglassHalf,
  faBriefcase,
  faCircleCheck,
  faTriangleExclamation,
  faBolt,
  faFileInvoice,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { QueueTable, QueueTableSkeleton } from './components/QueueTable';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';
import { PlatformTemplateUpdatesWidget } from './components/PlatformTemplateUpdatesWidget';

export function OwnerDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.firstName ?? 'there';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-owner'],
    queryFn: dashboardService.getOwnerDashboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Business overview — all operations at a glance.
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      {/* Phase 12.5C — renders only when a customised template has a platform
          update pending, so it adds nothing to the dashboard until it matters. */}
      <PlatformTemplateUpdatesWidget />

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          KPIs
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              <KpiCard label="Total Customers" value={data.kpis.totalCustomers} icon={faUsers} />
              <KpiCard
                label="Total Equipment"
                value={data.kpis.totalEquipment}
                icon={faGears}
                iconCls="bg-accent/20 text-accent-foreground"
              />
              <KpiCard
                label="Open Inward Records"
                value={data.kpis.openIntakes}
                icon={faClipboardList}
                iconCls="bg-secondary text-secondary-foreground"
              />
              <KpiCard
                label="Active Inspections"
                value={data.kpis.activeInspections}
                icon={faHourglassHalf}
                iconCls="bg-amber-500/10 text-amber-700"
              />
              <KpiCard
                label="Engineers Active Today"
                value={data.kpis.engineersActiveToday}
                icon={faUserCheck}
                iconCls="bg-teal-500/10 text-teal-700"
              />
              <KpiCard
                label="Pending Ops Reviews"
                value={data.kpis.pendingOpsReviews}
                icon={faBriefcase}
                iconCls="bg-purple-500/10 text-purple-700"
              />
              <KpiCard
                label="Pending Approvals"
                value={data.kpis.pendingCustomerApprovals}
                icon={faCircleCheck}
                iconCls="bg-green-500/10 text-green-700"
              />
              <KpiCard
                label="Overdue Items"
                value={data.kpis.overdueItems}
                icon={faTriangleExclamation}
                iconCls="bg-destructive/10 text-destructive"
              />
              <KpiCard
                label="Open Work Orders"
                value={data.kpis.openWorkOrders}
                icon={faFileInvoice}
                isFuture
              />
              <KpiCard
                label="Pending Dispatches"
                value={data.kpis.pendingDispatches}
                icon={faBolt}
                isFuture
              />
            </>
          ) : null}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <QueueTableSkeleton title="Unassigned Inspections" />
              <QueueTableSkeleton title="Overdue Scheduled" />
            </>
          ) : data ? (
            <>
              <QueueTable
                title="Unassigned Inspections"
                items={data.queues.unassigned}
                emptyMessage="All inspections are assigned."
              />
              <QueueTable
                title="Overdue Scheduled"
                items={data.queues.overdueScheduled}
                emptyMessage="No overdue inspections."
              />
              <QueueTable
                title="Awaiting Ops Review"
                items={data.queues.awaitingOpsReview}
                emptyMessage="No inspections pending operations review."
              />
            </>
          ) : null}
        </div>

        <div>
          {isLoading ? (
            <RecentActivitySkeleton />
          ) : data ? (
            <RecentActivityFeed items={data.recentActivity} />
          ) : null}
        </div>
      </section>
    </div>
  );
}
